import React from 'react';
import { REXY_FONT, REXY_CONSOLE } from '../../lib/rexyConsole';
import { useReports } from '../../data/dashboard';
import type { ReportEntry } from '../../data/dashboard';

const MONO = REXY_FONT;
const REPORT_OK = '#00FF9C';

// ─────────────────────────────────────────────────────────────────────────────
// REPORTS — HER 6-HOUR SUMMARY OVER HER OWN NOTES (Step 3 Piece 2).
// She reads her last window of Tier-2 NOTES and reasons them into ONE recounting
// in her own words (deep lane). content is the verbatim authored body — the UI
// mirrors it exactly, never synthesizes. Boundaries: 6am (always delivers),
// 12pm/6pm/12am (accept-or-reject). A window not listened to is put to the side
// and MERGES into the next — a re-reasoned natural mesh, more condensed, nothing
// dropped (every note stays in Tier 2 to drill into).
// ─────────────────────────────────────────────────────────────────────────────

// DESKTOP ARRANGEMENT ONLY. The contract types, the two endpoints and the
// report-answer GATE moved to data/dashboard/useReports.ts under the 2026-07-31
// "one core, every window" ruling — the phone surface answers through the SAME
// contract, never a side path. The render below is unchanged.
// Types re-exported so existing importers of this module keep resolving.
export type {
  ReportWindowSpan, ReportEntry, ReportsGate, ReportsView, ReportsResponse,
} from '../../data/dashboard';

const STATE: Record<ReportEntry['state'], { glyph: string; text: string; color: string }> = {
  delivered: { glyph: '✓', text: 'delivered', color: REPORT_OK },
  pending_delivery: { glyph: '◌', text: 'pending — sending', color: REXY_CONSOLE.warning },
  waiting: { glyph: '◔', text: 'waiting — listen or set aside', color: REXY_CONSOLE.gold },
  carried: { glyph: '→', text: 'set aside — carries into the next', color: REXY_CONSOLE.dimText },
  superseded: { glyph: '⇢', text: 'merged forward', color: REXY_CONSOLE.dimText },
  failed: { glyph: '✗', text: 'failed — never landed', color: REXY_CONSOLE.critical },
};

const KIND_LABEL: Record<ReportEntry['kind'], string> = {
  scheduled_6am: 'always delivers',
  boundary: 'accept / reject',
  asked: 'asked',
};

export default function ReportsPanel() {
  const {
    view, err, updatedAt, answering, heardNow, answer, timeLabel, stampLabel,
  } = useReports();

  function windowLabel(r: ReportEntry): string {
    const spans = r.windows || [];
    if (spans.length === 0) return '—';
    const first = spans[0];
    const last = spans[spans.length - 1];
    const from = first.start ? timeLabel(first.start) : 'start';
    return `${from} → ${timeLabel(last.end)}`;
  }

  function renderReport(r: ReportEntry) {
    const st = STATE[r.state] || STATE.delivered;
    const failed = r.status === 'record_failed';
    return (
      <div key={r.report_id} style={styles.report} title={r.report_id}>
        <div style={styles.reportHeader}>
          <span style={styles.boundary}>{r.boundary || timeLabel(r.composed_at)}</span>
          <span style={{ ...styles.glyph, color: st.color }}>{st.glyph}</span>
          <span style={{ ...styles.state, color: st.color }}>{st.text}</span>
          {r.merged ? (
            <span style={styles.mergedBadge}>MERGED ×{r.windows.length}</span>
          ) : null}
          <span style={styles.kind}>{KIND_LABEL[r.kind] || r.kind}</span>
        </div>
        <div style={styles.windowRow}>
          <span style={styles.windowSpan}>{windowLabel(r)}</span>
          <span style={styles.noteCount}>
            {r.notes_total} note{r.notes_total === 1 ? '' : 's'}
          </span>
          {r.mirror === 'mismatch' || r.mirror === 'missing' ? (
            <span style={styles.recordFailed}>mirror {r.mirror}</span>
          ) : null}
          {failed ? <span style={styles.recordFailed}>tier-2 record failed</span> : null}
        </div>
        {/* Her report, VERBATIM — the TIER-2 record body (mirror-enforced read),
            never re-rendered prose. A just-accepted report renders the ANSWER
            RESPONSE's returned body instantly (LISTEN shows it now). */}
        <div style={heardNow[r.report_id] ? styles.contentHeard : styles.content}>
          {heardNow[r.report_id] || r.content}
        </div>
        {/* Answerable ONLY when complete AND waiting — a failed report never
            offers LISTEN/SET ASIDE (the false-success inversion, closed). */}
        {r.state === 'waiting' && r.status === 'complete' ? (
          <div style={styles.answerRow}>
            <button
              style={styles.acceptBtn}
              disabled={answering === r.report_id}
              onClick={() => answer(r.report_id, 'accept')}
            >
              LISTEN
            </button>
            <button
              style={styles.rejectBtn}
              disabled={answering === r.report_id}
              onClick={() => answer(r.report_id, 'reject')}
            >
              SET ASIDE
            </button>
          </div>
        ) : null}
        {r.state === 'superseded' && r.superseded_by ? (
          <div style={styles.supersededNote}>re-reasoned into {r.superseded_by}</div>
        ) : null}
      </div>
    );
  }

  return (
    <div style={styles.card}>
      <div style={styles.headerRow}>
        <div style={styles.sectionHeader}>{'║'} REPORTS</div>
        <div style={styles.subtitle}>
          6-hour windows {'·'} {view?.timezone || 'world clock'}
          {updatedAt ? <span> {'·'} updated {stampLabel(updatedAt)}</span> : null}
        </div>
      </div>
      {err ? (
        <div style={styles.degraded}>not_available {'—'} {err}</div>
      ) : !view ? (
        <div style={styles.degraded}>loading{'…'}</div>
      ) : view.store_status === 'unreadable' ? (
        <div style={styles.critical}>report store unreadable {'—'} visible failure</div>
      ) : (
        <>
          {view.pending ? (
            <div style={styles.reminder}>{view.pending.reminder}</div>
          ) : null}
          {view.gate?.waiting_on_note ? (
            <div style={styles.waitingNote}>
              boundary due {'—'} waiting for the hour{"'"}s note to land (complete-after-landed-note)
            </div>
          ) : null}
          {view.windows.length === 0 ? (
            <div style={styles.emptyHistory}>no reports composed yet</div>
          ) : (
            <div style={styles.reports}>{view.windows.map(renderReport)}</div>
          )}
        </>
      )}
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  card: { border: `1px solid ${REXY_CONSOLE.cardBorder}`, background: REXY_CONSOLE.cardBg, borderRadius: 6, padding: 14, boxShadow: '0 4px 14px rgba(0,0,0,0.45), inset 0 1px 0 rgba(0,191,255,0.06)', minWidth: 0, maxWidth: '100%', overflowWrap: 'anywhere' },
  // Title sits at the TOP of the panel; the meta strap sits UNDER it and wraps, so it
  // can never displace the title or overflow the card edge (owner ruling 2026-07-24).
  headerRow: { display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 3, marginBottom: 10, minWidth: 0 },
  sectionHeader: { fontWeight: 700, fontFamily: MONO, fontSize: 12, letterSpacing: 2, color: REXY_CONSOLE.primary },
  subtitle: { fontFamily: MONO, fontSize: 9, letterSpacing: 1, color: REXY_CONSOLE.dimText, whiteSpace: 'normal', overflowWrap: 'anywhere', maxWidth: '100%' },
  reminder: { fontFamily: MONO, fontSize: 10, color: REXY_CONSOLE.gold, border: `1px solid ${REXY_CONSOLE.badgeWrnBg}`, background: REXY_CONSOLE.badgeWrnBg, borderRadius: 4, padding: '5px 8px', marginBottom: 8 },
  waitingNote: { fontFamily: MONO, fontSize: 9, color: REXY_CONSOLE.warning, padding: '2px 0 6px 0' },
  reports: { display: 'flex', flexDirection: 'column', gap: 10 },
  report: { borderTop: `1px solid ${REXY_CONSOLE.rowDivider}`, padding: '8px 4px 4px 4px' },
  reportHeader: { display: 'flex', alignItems: 'baseline', gap: 8 },
  boundary: { fontFamily: MONO, fontSize: 11, color: REXY_CONSOLE.primary, minWidth: 40 },
  glyph: { fontFamily: MONO, fontSize: 11 },
  state: { fontFamily: MONO, fontSize: 9, letterSpacing: 1 },
  mergedBadge: { fontFamily: MONO, fontSize: 8, letterSpacing: 1, color: REXY_CONSOLE.gold, border: `1px solid ${REXY_CONSOLE.badgeWrnBg}`, borderRadius: 3, padding: '1px 4px' },
  kind: { fontFamily: MONO, fontSize: 8, letterSpacing: 1, color: REXY_CONSOLE.dimText, marginLeft: 'auto', whiteSpace: 'nowrap' },
  windowRow: { display: 'flex', alignItems: 'baseline', gap: 8, marginTop: 3 },
  windowSpan: { fontFamily: MONO, fontSize: 9, color: REXY_CONSOLE.mutedText },
  noteCount: { fontFamily: MONO, fontSize: 9, color: REXY_CONSOLE.dimText },
  recordFailed: { fontFamily: MONO, fontSize: 9, color: REXY_CONSOLE.critical, marginLeft: 'auto' },
  content: { fontFamily: MONO, fontSize: 10, lineHeight: 1.55, color: REXY_CONSOLE.infText, whiteSpace: 'pre-wrap', overflowWrap: 'anywhere', marginTop: 6, borderLeft: `2px solid ${REXY_CONSOLE.divider}`, paddingLeft: 8 },
  contentHeard: { fontFamily: MONO, fontSize: 10, lineHeight: 1.55, color: REXY_CONSOLE.secondary, whiteSpace: 'pre-wrap', overflowWrap: 'anywhere', marginTop: 6, borderLeft: `2px solid ${REPORT_OK}`, paddingLeft: 8 },
  answerRow: { display: 'flex', gap: 8, marginTop: 7 },
  acceptBtn: { fontFamily: MONO, fontSize: 9, letterSpacing: 1, color: '#001018', background: REPORT_OK, border: 'none', borderRadius: 3, padding: '3px 10px', cursor: 'pointer' },
  rejectBtn: { fontFamily: MONO, fontSize: 9, letterSpacing: 1, color: REXY_CONSOLE.mutedText, background: 'transparent', border: `1px solid ${REXY_CONSOLE.divider}`, borderRadius: 3, padding: '3px 10px', cursor: 'pointer' },
  supersededNote: { fontFamily: MONO, fontSize: 8, color: REXY_CONSOLE.dimText, marginTop: 4 },
  critical: { fontFamily: MONO, fontSize: 10, color: REXY_CONSOLE.critical, padding: '4px 0' },
  degraded: { fontFamily: MONO, fontSize: 10, color: REXY_CONSOLE.dimText, padding: '4px 0' },
  emptyHistory: { fontFamily: MONO, fontSize: 9, color: REXY_CONSOLE.dimText, padding: '4px 0' },
};
